import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { InputGroup } from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import * as Yup from "yup";
import { BorderColorOutlined } from "@mui/icons-material";
import { PimConnection } from "../../store/slices/pimConnection";
import { saveCredentials } from "../../store/slices/saveCredentials";
import { getFilterData } from "../../store/slices/getFilterData";
import { parseShopifyHost } from '../../utils/parseShopifyHost';
import { createSassStore } from "../../store/slices/createStore";
import { getConfig } from "../../store/slices/getConfig";
import { saveProductMapping } from "../../store/slices/saveProductMapping";

const validationSchema = Yup.object().shape({
  url: Yup.string()
    .required("URL is Required")
    .matches(/^https:\/\//, "please provide a secure URL (e.g. https://www.xyz.abc.com)"),
  secret: Yup.string().required("Secret is Required"),
  clientId: Yup.string().required("Client ID is Required"),
  username: Yup.string().required("Username is Required"),
  password: Yup.string().required("Password is Required"),
  storeName: Yup.string().required("Store URL is Required"),
  displayName: Yup.string().required("Display name is Required"),
});

function Connection({ showToast }) {
  const [buttonClicked, setButtonClicked] = useState(null);
  const [disabledField, setDisabledField] = useState(true);
  const [disabledSaveConfig, setDisabledSaveConfig] = useState(false);
  const dispatch = useDispatch();
  const savedData = useSelector((state) => state.config.data);
  const struConfigId = useSelector((state) => state.config.struConfigId);
  // const shopifyAppData = useSelector(state => state.shopifyAppConfig?.data?.shopifyAppInfoData)
  const shopifyAppData = useSelector(state => state.shopifyAppConfig?.data)
  const { isShopifyApp, shopName } = parseShopifyHost();
  const [toggle, setToggle] = useState(savedData?.attributeMapping?.isPreExistingCatalog === "true" ? "true" : "false");
  const isInitConfig = useSelector(state => state.shopifyAppConfig?.isinitConfigExist);
  const configId = useSelector((state) => state.saveCreds.configId);
  const pimConnectionStatus = useSelector(state => state.data);

  const formik = useFormik({
    initialValues: {
      url: "",
      secret: "",
      clientId: "",
      username: "",
      password: "",
      storeName: "",
      accessToken: "",
      displayName: "",
      shopifyClientId: "",
      shopifySecret: ""
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values, { setSubmitting }) => {
      if (buttonClicked != null && buttonClicked === "testConnection") {
        const submitValue = {
          url: values.url,
          secret: values.secret,
          clientId: values.clientId,
          username: values.username,
          password: values.password,
        };

        showToast("testConnection");
        dispatch(PimConnection(submitValue));
        setSubmitting(false);
      } else {
        if(isShopifyApp && isInitConfig){

        const saveCred = {
          clientDisplayName: values.displayName,
          pimCredential: {
            url: values.url,
            secret: values.secret,
            clientId: values.clientId,
            username: values.username,
            password: values.password,
          },
          shopifyCredential: {
            storeName: values.storeName,
            accessToken: values.accessToken,
            secret: values.shopifySecret,
            clientId: values.shopifyClientId
          }
        };

         dispatch(saveCredentials(saveCred)).then((action) => {
          showToast("saveCredentials");
          const response = dispatch(getConfig(values.storeName)).then((action)=>{
            const req = {
              attributeMapping: {
              ...savedData?.attributeMapping,
              isPreExistingCatalog: toggle ? toggle : "false",
              isPreExistingCategoriesCompleted: "null",
              isPreExistingProductCompleted: "null",
              },
              struConfigId: configId || action?.payload?.data?.data?.struConfigId
            }
            dispatch(saveProductMapping(req)).then((action)=>{
              window.location.reload();
            })
          })
        });

        }else{
        const saveCred = {
          clientDisplayName: values.displayName,
          pimCredential: {
            url: values.url,
            secret: values.secret,
            clientId: values.clientId,
            username: values.username,
            password: values.password,
          },
          shopifyCredential: {
            storeName: values.storeName,
            accessToken: values.accessToken,
            secret: values.shopifySecret,
            clientId: values.shopifyClientId
          },
          struConfigId: struConfigId
        };


        showToast("saveCredentials");

        setTimeout(() => {
          dispatch(saveCredentials(saveCred));
          setSubmitting(false);
        }, 400);
      }
    }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } = formik;

  const toggleField = useCallback(() => {
    setDisabledField(prev => !prev);
  }, []);

  const handleDisplayNameBlur = useCallback(() => {
    if (values.displayName.trim() !== "") {
      setDisabledField(true);
    }
  }, [values.displayName]);

  const handleDisplayNameFocus = useCallback(() => {
    setDisabledField(false);
  }, []);

  const handleTestConnection = useCallback(() => {
    setButtonClicked("testConnection");
  }, []);

  const handleSaveConfig = useCallback(() => {
    setButtonClicked("all");
  }, []);

  // Memoize parsed credential data
  const parsedCredentials = useMemo(() => {
    if (!savedData) return null;

    try {
      const pimData = savedData.sourceClientCredential
        ? JSON.parse(savedData.sourceClientCredential)
        : {};
      const shopifyData = savedData.targetClientCredential
        ? JSON.parse(savedData.targetClientCredential)
        : {};

      if (!pimData & !shopifyData) {
        setDisabledSaveConfig(true);
      }

      return {
        pimData,
        shopifyData,
        displayName: savedData.clientDisplayName || ""
      };
    } catch (error) {
      console.error("Error parsing credentials:", error);
      return null;
    }
  }, [savedData]);

  useEffect(()=>{
    if(isShopifyApp && isInitConfig &&  pimConnectionStatus?.status === "success"){
      setDisabledSaveConfig(false)
    }
    else if(isShopifyApp && isInitConfig && pimConnectionStatus?.status !== "success"){
      setDisabledSaveConfig(true)
    }
  },[pimConnectionStatus])

  // Set form values from saved data
  useEffect(() => {
    if (parsedCredentials) {
      const { pimData, shopifyData, displayName } = parsedCredentials;
      formik.setValues({
        url: pimData.url || "",
        secret: pimData.secret || "",
        clientId: pimData.clientId || "",
        username: pimData.username || "",
        password: pimData.password || "",
        storeName: shopifyData.storeName || "",
        accessToken: shopifyData.accessToken || "",
        shopifyClientId : shopifyData.clientId ||  "",
        shopifySecret : shopifyData.secret || "",
        displayName: displayName,
      });
    }else if(isShopifyApp){
       formik.setValues({
        url: "",
        secret: "",
        clientId: "",
        username: "",
        password: "",
        storeName: shopifyAppData?.shopifyAppInfoData?.targetStoreDomain || "",
        accessToken: shopifyAppData?.shopifyAppInfoData?.credential?.access_token || "",
        shopifyClientId : "",
        shopifySecret : "",
        displayName: "",
      });
    }
  }, [parsedCredentials, formik.resetForm, shopifyAppData]);

  // Fetch filter data when savedData changes
  useEffect(() => {
    if (savedData?.struConfigId) {
      const locale = savedData?.basicFilter?.dataFilter?.locale || "en_US";
      dispatch(getFilterData({
        configId: savedData.struConfigId,
        locale
      }));
    }
  }, [savedData?.struConfigId]);

  return (
    <Form onSubmit={handleSubmit} autoComplete="off">
      <Accordion.Item eventKey="0" className="my-1 mt-4">
        <Accordion.Header>Connections</Accordion.Header>
        <Accordion.Body className="p-4">
          <div className="bg-light p-4 rounded">
            <h6 className="fw-bold mb-4">PIM API Details</h6>
            <Row>
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="url">
                  <Form.Label>PIM URL</Form.Label>
                  <Form.Control
                    type="url"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.url}
                    autoComplete="off"
                    isInvalid={touched.url && errors.url}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.url}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="clientId">
                  <Form.Label>Client ID</Form.Label>
                  <Form.Control
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.clientId}
                    autoComplete="off"
                    isInvalid={touched.clientId && errors.clientId}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.clientId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3" controlId="secret">
              <Form.Label>Secret ID</Form.Label>
              <Form.Control
                type="password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.secret}
                autoComplete="new-password"
                isInvalid={touched.secret && errors.secret}
              />
              <Form.Control.Feedback type="invalid">
                {errors.secret}
              </Form.Control.Feedback>
            </Form.Group>
            <Row className="mb-2">
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="username">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.username}
                    autoComplete="off"
                    isInvalid={touched.username && errors.username}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.username}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="password">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    autoComplete="new-password"
                    isInvalid={touched.password && errors.password}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="bg-light p-4 rounded mt-4">
            <h6 className="fw-bold mb-4">Shopify API Details</h6>
            <Row className="mb-2">
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="storeName">
                  <Form.Label>Store URL</Form.Label>
                  <Form.Control
                    type="text"
                    readOnly
                    disabled
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.storeName}
                    autoComplete="off"
                    isInvalid={touched.storeName && errors.storeName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.storeName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="accessToken">
                  <Form.Label>Access Token</Form.Label>
                  <Form.Control
                    type="password"
                    readOnly
                    disabled
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.accessToken}
                    autoComplete="off"
                    isInvalid={touched.accessToken && errors.accessToken}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.accessToken}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="ShopifyClientId">
                  <Form.Label>Client ID</Form.Label>
                  <Form.Control
                    type="text"
                    readOnly
                    disabled
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.shopifyClientId}
                    autoComplete="off"
                    isInvalid={touched.shopifyClientId && errors.shopifyClientId}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.shopifyClientId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group className="mb-3" controlId="shopifySecret">
                  <Form.Label>Secret ID</Form.Label>
                  <Form.Control
                    type="password"
                    readOnly
                    disabled
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.shopifySecret}
                    autoComplete="off"
                    isInvalid={touched.shopifySecret && errors.shopifySecret}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.shopifySecret}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col sm={12}>
                <Form.Group className="mb-3" controlId="displayName">
                  <Form.Label>Display Name</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      onChange={handleChange}
                      onBlur={handleDisplayNameBlur}
                      onFocus={handleDisplayNameFocus}
                      value={values.displayName}
                      autoComplete="off"
                      disabled={disabledField}
                      isInvalid={touched.displayName && errors.displayName}
                    />
                    {disabledField && (
                      <InputGroup.Text className="p-0 border-start-0 editIcon">
                        <Button
                          variant="light"
                          className={`border-0 ${disabledField ? "disabled-icon" : "bg-white"}`}
                          onClick={toggleField}
                        >
                          <BorderColorOutlined />
                        </Button>
                      </InputGroup.Text>
                    )}
                    <Form.Control.Feedback type="invalid">
                      {errors.displayName}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="bg-light p-4 rounded mt-4">
            <p className="fw-bold">Do you have an existing catalog on Shopify?</p>
            <label className="lbl">
              <input
                type="radio"
                className="custom-radio"
                value="true"
                // checked={savedData?.attributeMapping?.isPreExistingCatalog ? savedData?.attributeMapping?.isPreExistingCatalog : "false" =="true"}
                 checked={(isShopifyApp && !isInitConfig) ? (savedData?.attributeMapping?.isPreExistingCatalog === "true") : toggle == "true"}
                disabled={isShopifyApp && !isInitConfig}
                onClick={(e)=>{ isShopifyApp && setToggle(e.target.value)}}
              />
              <span className="radiolabel">Yes</span>
            </label>

            <label className="lbl">
              <input
                type="radio"
                className="custom-radio"
                value="false"
                // checked={savedData?.attributeMapping?.isPreExistingCatalog ? savedData?.attributeMapping?.isPreExistingCatalog : "false"== "false"}
                checked={(isShopifyApp && !isInitConfig)? (savedData?.attributeMapping?.isPreExistingCatalog === "false" || savedData?.attributeMapping?.isPreExistingCatalog === undefined) : toggle =="false"}
                disabled={isShopifyApp && !isInitConfig}
                onClick={(e)=>{isShopifyApp && setToggle(e.target.value)}}
              />
              <span className="radiolabel">No</span>
            </label>
          </div>

          <Row className="mt-5 mx-3">
            <Col sm={6}>
              <Button
                variant="primary"
                type="submit"
                className="stru-btn"
                onClick={handleSaveConfig}
                disabled={disabledSaveConfig}
              >
                Save Config
              </Button>
            </Col>
            <Col sm={6}>
              <Button
                variant="primary"
                type="submit"
                className="stru-btn test-conn float-end"
                onClick={handleTestConnection}
              >
                Test PIM Connection
              </Button>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>
    </Form>
  );
}

export default Connection;